import { noop } from 'lodash';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'ui-components';
import TabTimeElapsed from '~/components/TabTimeElapsed';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import { useSavedListSendMessage } from '~/hooks/api/useSavedListSendMessage';
import { useGetSharedSavedList } from '~/hooks/api/useSavedLists';
import {
  ViewOptionsMappedType,
  mapViewFromApi,
  viewTypeToViewOptionsMapper,
} from '~/hooks/api/useSavedLists/useCreateSharedLink';
import { useSharedLinkAccessEmail } from '~/hooks/api/useSavedLists/useSharedLinkAccessEmail';
import {
  useGetShareLink,
  useSetShareLinkAccess,
  useUpdateShareLinkAccess,
} from '~/hooks/api/useShareLinks';
import { EmailAccessScreen } from '~/screens/EmailAccessScreen';
import { ICreator } from '~/types';

import {
  MessageToolFormValues,
  MessageToolModal,
} from '../MessageToolModal/MessageToolModal';
import { useMessageToolStore } from '../MessageToolModal/useMessageToolStore';
import { useActiveView } from '../MyAgency/hooks/useActiveView';
import { SharedSavedListContent } from '../SharedSavedListContent';
import { SUCCESS_STATUS } from './constants';

export const SHARED_TOKEN_PREFIX = 'share-link-';

export const SharedSavedListScreen = () => {
  const [shareLinkAccessId, setShareLinkAccessId] = useState(null);
  const [isEmailAccessOpen, setIsEmailAccessOpen] = useState(false);

  const snackbar = useSnackbar();

  const router = useRouter();
  const { id: token, view: pathView } = router.query as {
    id: string;
    view: string;
  };

  const { data: link, status } = useGetShareLink({
    sharedToken: token,
    params: {
      show_owners: true,
    },
  });
  const createSharelinkAccess = useSetShareLinkAccess();
  const updateSharelinkAccess = useUpdateShareLinkAccess();
  const { accessEmail, updateAccessEmail } = useSharedLinkAccessEmail();
  const { openModal: openMessageToolModal } = useMessageToolStore();

  const isSharedLinkAvailable = status === SUCCESS_STATUS && link;
  const sharedToken = link?.token && `${SHARED_TOKEN_PREFIX}${link?.token}`;
  const view = link?.options?.view as ViewOptionsMappedType;

  const { data: list } = useGetSharedSavedList({
    sharedToken,
  });
  const sendMessage = useSavedListSendMessage({ sharedToken });

  const { activeView, setActiveView } = useActiveView();

  const updateShareLinkAccess = useCallback(
    (payload: { email?: string; time?: number }) => {
      if (!(shareLinkAccessId && link)) return;

      updateSharelinkAccess.mutate({
        shareLinkId: link.id,
        shareLinkAccessId,
        ...payload,
      });
    },
    [shareLinkAccessId, link, updateSharelinkAccess],
  );

  const handleSubmit = useCallback(
    (email: string) => {
      if (!list) return;

      updateAccessEmail(email);
      setIsEmailAccessOpen(false);
      updateShareLinkAccess({ email });

      trackEvent('foam-shared-link-email-submitted', {
        name: list.name,
        agency: list.agencyName || '',
        email: email,
      });
    },
    [updateAccessEmail, setIsEmailAccessOpen, updateShareLinkAccess, list],
  );

  const handleSubmitMessage = useCallback(
    async (values: MessageToolFormValues & { creator: ICreator }) => {
      if (!list || !link) return;
      const { message, name, email, company, creator } = values;
      const sharedLinktOwnerId = link?.owners?.find(
        (owner) => owner.ownerType === 'manager',
      )?.ownerId;

      await sendMessage.mutateAsync(
        {
          creatorId: creator?.id || '',
          message,
          name,
          email,
          company,
          savedListId: list?.id || '',
          sharedLinktOwnerId: sharedLinktOwnerId || '',
        },
        {
          onSuccess: () => {
            snackbar.success({
              description: 'Message succesfully sent',
            });
          },
          onError: () => {
            snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
          },
        },
      );
    },
    [list, sendMessage, snackbar, link],
  );

  const handleOpenMessageToolModal = useCallback(
    (creator: ICreator) => {
      if (!list || !link || !creator) return;
      openMessageToolModal({ creator, list, link });
    },
    [openMessageToolModal, list, link],
  );

  useEffect(() => {
    if (list === undefined || link === undefined) return;

    trackEvent('foam-shared-link-view', {
      type: link.entityType,
      category: list.category,
      name: list.name,
      agency: list.agencyName || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link?.entityType, list?.category, list?.name, list?.agencyName]);

  useEffect(() => {
    if (pathView in viewTypeToViewOptionsMapper) {
      setActiveView(mapViewFromApi(pathView as ViewOptionsMappedType));
    } else {
      view && setActiveView(mapViewFromApi(view));
    }
  }, [view, pathView, setActiveView]);

  useEffect(() => {
    link && setIsEmailAccessOpen(link.isEmailRequired && !accessEmail);
  }, [link, list, accessEmail]);

  // This effect is used to create a shareLinkAccess
  useEffect(() => {
    if (!isSharedLinkAvailable) return;

    createSharelinkAccess.mutate(
      {
        shareLinkId: link.id,
        email: accessEmail,
      },
      {
        onSuccess: ({ data: linkAccess }) => {
          setShareLinkAccessId(linkAccess.id);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharedLinkAvailable]);

  const handleOnLeaveTab = useCallback(
    (time: number) => updateShareLinkAccess({ time }),
    [updateShareLinkAccess],
  );

  const pageContent = useMemo(
    () =>
      list && (
        <>
          <SharedSavedListContent
            activeView={activeView}
            setActiveView={setActiveView}
            list={list}
            sharedToken={sharedToken}
            onNoteOpen={noop}
            onMessageOpen={handleOpenMessageToolModal}
            link={link}
          />
          <MessageToolModal onSubmit={handleSubmitMessage} />
        </>
      ),
    [activeView, link, list, setActiveView, sharedToken, openMessageToolModal],
  );

  const emailAccessModal = useMemo(
    () =>
      list && (
        <EmailAccessScreen
          agencyName={list.agencyName || ''}
          agencyLogo={list.agencyLogo}
          isOpen={isEmailAccessOpen}
          listName={list.name}
          managerName={list.manager?.name || list.agencyName}
          onSubmit={handleSubmit}
        />
      ),
    [list, isEmailAccessOpen, handleSubmit],
  );

  return (
    <>
      {shareLinkAccessId && <TabTimeElapsed onLeaveTab={handleOnLeaveTab} />}
      {pageContent}
      {emailAccessModal}
    </>
  );
};
